import { useState, useEffect, useRef, useLayoutEffect } from "react";

import CustomSearch from "./CustomSearch";
import Button from "../../Buttons/Button";
import AddOptionPopup from "../../Modal/AddOptionPopup";
import Chip from "../../Chip/Chip";
import { createPortal } from "react-dom";
import { formatWords2 } from "../../../utils/stringUtils";

const sizeClasses = {
  xs: "px-2 py-0",
  "xs-sm": "px-2.5 py-1",
  sm: "px-3 py-1.5",
  md: "px-3 py-2",
  lg: "px-4 py-2.5",
  xl: "px-5 py-3",
};

const fontSizeClasses = {
  xs: "text-[12px]",
  "xs-sm": "text-[12px]",
  sm: "text-[12px]",
  md: "text-[14px]",
  lg: "text-[16px]",
  xl: "text-[18px]",
};

const iconSizeClasses = {
  xs: "!text-[12px]",
  "xs-sm": "!text-[14px]",
  sm: "!text-[14px]",
  md: "!text-[14px]",
  lg: "!text-[15px]",
  xl: "!text-[15px]",
};

const CustomSelect = ({
  options = [],
  selected,
  onChange,
  placeholder = "Select an option",
  showSearch = false,
  size = "md",
  rounded = "rounded",
  showButton = false,
  alertName,
  icon = false,
  minwidth = "min-w-32",
  disabled,
  refData,
  handleScroll,
  keyname,
  searchValue = "",
  onSearch,
  onClose,
  multiple = false,
  style = "normal", // normal, chip
  fetchedOriginalData,
  onSelectItem,
  noBorder = false,
  shadow = false,
  showClearButton = false, // to clear the input value
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [customOptions, setCustomOptions] = useState(options || []);
  const [selectedOptions, setSelectedOptions] = useState(multiple ? [] : null);
  const [visibleLabels, setVisibleLabels] = useState([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const dropdownRef = useRef(null);
  const selectedLabelsRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownContainerRef = useRef(null);
  const [isRightAligned, setIsRightAligned] = useState(false);
  const [isBelowPositioned, setIsBelowPositioned] = useState(true);
  const shouldPersist = rest["data-dropdown-persist"] === "true";

  // Initialize selected options from props
  useEffect(() => {
    if (selected !== undefined) {
      if (multiple) {
        // For multiple selection, ensure we have an array
        const selectedArray = Array.isArray(selected) ? selected : [];

        // Convert any value-only items to full option objects
        const fullOptions = selectedArray
          .map((item) => {
            if (
              typeof item === "object" &&
              item !== null &&
              item.value !== undefined
            ) {
              return item;
            } else {
              // Find the matching option from options array
              const matchingOption = customOptions.find(
                (opt) => opt && String(opt.value) === String(item),
              );
              return matchingOption || { value: item, label: String(item) };
            }
          })
          .filter(Boolean); // Filter out any null/undefined values
        setSelectedOptions(fullOptions);
      } else {
        // For single selection
        if (
          typeof selected === "object" &&
          selected !== null &&
          selected.value !== undefined
        ) {
          setSelectedOptions(selected);
        } else {
          // Find the matching option from options array
          const matchingOption = customOptions.find(
            (opt) => opt && String(opt.value) === String(selected),
          );

          // If no matching option is found, create a temporary one with the value
          // This ensures something is displayed even if options haven't loaded yet
          setSelectedOptions(
            matchingOption || { value: selected, label: String(selected) },
          );
        }
      }
    } else {
      setSelectedOptions(multiple ? [] : null);
    }
  }, [selected, customOptions, multiple]);

  // Update options when props change
  useEffect(() => {
    if (options && Array.isArray(options)) {
      setCustomOptions(options);
    }
  }, [options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if click is inside the dropdownRef (button) or the dropdownContainerRef (portal dropdown)
      if (
        (dropdownRef.current && dropdownRef.current.contains(event.target)) ||
        (dropdownContainerRef.current &&
          dropdownContainerRef.current.contains(event.target))
      ) {
        return;
      }

      // Check if the clicked element or any parent has data-dropdown-persist="true"
      let element = event.target;
      while (element) {
        if (
          element.getAttribute &&
          element.getAttribute("data-dropdown-persist") === "true"
        ) {
          return; // Don't close dropdown if persist attribute is found
        }
        element = element.parentElement;
      }

      setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      if (onClose) {
        onClose();
      }
    }
  }, [isOpen, onClose]);

  // Calculate visible labels based on container width for multiple selection
  useEffect(() => {
    if (
      multiple &&
      selectedOptions &&
      Array.isArray(selectedOptions) &&
      selectedOptions.length > 0 &&
      selectedLabelsRef.current
    ) {
      const calculateVisibleLabels = () => {
        const containerWidth = selectedLabelsRef.current.offsetWidth;
        const tempSpan = document.createElement("span");
        tempSpan.style.visibility = "hidden";
        tempSpan.style.position = "absolute";
        tempSpan.style.fontSize = fontSizeClasses[size].replace("text-", "");
        document.body.appendChild(tempSpan);

        let totalWidth = 0;
        let visibleCount = 0;

        // Add labels until we run out of space
        for (let i = 0; i < selectedOptions.length; i++) {
          if (!selectedOptions[i]) continue;

          // Calculate width based on style type
          if (style === "chip") {
            // For chip style, truncate text if needed
            const displayText =
              selectedOptions[i].label && selectedOptions[i].label.length > 20
                ? selectedOptions[i].label.substring(0, 20) + "..."
                : selectedOptions[i].label || String(selectedOptions[i].value);

            tempSpan.textContent = displayText;
            // Add extra width for chip styling and close button
            const labelWidth = tempSpan.offsetWidth + 24; // Extra padding for chip style

            if (totalWidth + labelWidth < containerWidth - 40) {
              totalWidth += labelWidth;
              visibleCount++;
            } else {
              break;
            }
          } else {
            // For normal style
            // We'll use a fixed max width for each item
            const maxItemWidth = 250; // Match the max-w-[150px] in the span

            // Calculate actual text width (for tooltip/title)
            tempSpan.textContent =
              selectedOptions[i].label || String(selectedOptions[i].value);
            const actualWidth = tempSpan.offsetWidth;

            // Use the smaller of actual width or max width, plus close button
            const labelWidth = Math.min(actualWidth, maxItemWidth) + 24; // 24px for padding and close icon

            if (totalWidth + labelWidth < containerWidth - 40) {
              totalWidth += labelWidth;
              visibleCount++;
            } else {
              break;
            }
          }
        }

        document.body.removeChild(tempSpan);

        setVisibleLabels(selectedOptions.slice(0, visibleCount));
        setHiddenCount(selectedOptions.length - visibleCount);
      };

      calculateVisibleLabels();

      // Recalculate on window resize
      window.addEventListener("resize", calculateVisibleLabels);
      return () => window.removeEventListener("resize", calculateVisibleLabels);
    } else {
      setVisibleLabels([]);
      setHiddenCount(0);
    }
  }, [selectedOptions, size, style, multiple]);

  // Filter options based on search input
  const filteredOptions = customOptions.filter((option) => {
    if (!option || typeof option.label !== "string") return false;

    const searchTerm = search || ""; // ensure it's a string
    // If search is empty, return all options
    if (searchTerm.trim() === "") {
      return true;
    }
    // Otherwise, filter based on label
    return option.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const AddNew = () => {
    setShowPopup(!showPopup);
  };

  const handleAddOption = (newOption) => {
    const updatedOptions = [...customOptions, newOption].sort((a, b) =>
      a.label.replace(/\s+/g, "").localeCompare(b.label.replace(/\s+/g, "")),
    );
    setCustomOptions(updatedOptions);
    handleOptionSelect(newOption);
  };

  // Handle option selection
  const handleOptionSelect = (option) => {
    if (multiple) {
      // For multiple selection
      let updatedSelection;

      // Check if option is already selected
      const isSelected = selectedOptions.some(
        (item) => item && String(item.value) === String(option.value),
      );

      if (isSelected) {
        // Remove the option if already selected
        updatedSelection = selectedOptions.filter(
          (item) => item && String(item.value) !== String(option.value),
        );
      } else {
        // Add the option if not already selected
        updatedSelection = [...selectedOptions, option];
      }

      setSelectedOptions(updatedSelection);

      // Call onChange with array of values
      if (onChange) {
        const values = updatedSelection.map((item) => item.value);
        onChange(values);
      }
    } else {
      // For single selection
      setSelectedOptions(option);
      if (onChange) {
        onChange(option.value);
      }
      if (onSelectItem) {
        onSelectItem(options, fetchedOriginalData, option.value, option);
      }
      setIsOpen(false);
    }
  };

  const handleRemoveOption = (optionToRemove) => {
    if (multiple && Array.isArray(selectedOptions)) {
      const newSelectedOptions = selectedOptions.filter(
        (option) =>
          option && String(option.value) !== String(optionToRemove.value),
      );
      setSelectedOptions(newSelectedOptions);
      if (onChange) {
        const values = newSelectedOptions.map((opt) => opt.value);
        onChange(values);
      }
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  useLayoutEffect(() => {
    if (isOpen && buttonRef.current && dropdownContainerRef.current) {
      const updatePosition = () => {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownRect =
          dropdownContainerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Calculate available space
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        // Determine if dropdown should be below or above button
        const showBelow =
          spaceBelow >= Math.min(320, dropdownRect.height) ||
          spaceBelow >= spaceAbove;

        // Calculate left position
        let left = buttonRect.left;
        // Adjust if dropdown would overflow right edge
        if (left + dropdownRect.width > viewportWidth) {
          left = Math.max(0, viewportWidth - dropdownRect.width);
        }

        // Calculate top position
        let top;
        if (showBelow) {
          top = buttonRect.bottom + 8; // Add 8px gap
          // Check if dropdown extends beyond bottom of viewport
          if (top + dropdownRect.height > viewportHeight) {
            top = viewportHeight - dropdownRect.height - 8;
          }
        } else {
          top = buttonRect.top - dropdownRect.height - 8; // Add 8px gap
          // Check if dropdown extends beyond top of viewport
          if (top < 0) {
            top = 8;
          }
        }

        // Apply the calculated position
        dropdownContainerRef.current.style.left = `${left}px`;
        dropdownContainerRef.current.style.top = `${top}px`;
        setIsBelowPositioned(showBelow);
      };

      // Initial position update
      updatePosition();

      // Update position on scroll
      const handleScroll = () => {
        requestAnimationFrame(updatePosition);
      };

      // Add scroll event listeners
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);

      // Cleanup
      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [isOpen]);

  // Get display value for the select button
  const getDisplayValue = () => {
    if (multiple) {
      if (
        !selectedOptions ||
        !Array.isArray(selectedOptions) ||
        selectedOptions.length === 0
      ) {
        return placeholder;
      }

      // For multiple selection with chips, we'll show the chips in the component
      return (
        <div
          ref={selectedLabelsRef}
          className="flex flex-wrap gap-1 max-w-full overflow-hidden"
        >
          {visibleLabels.map(
            (option, index) =>
              option && (
                <div key={index} className="flex items-center">
                  {style === "chip" ? (
                    <Chip
                      label={option.label || String(option.value)}
                      size="xs"
                      onDelete={() => handleRemoveOption(option)}
                    />
                  ) : (
                    <span
                      className="bg-info-50 px-2 py-0.5 rounded-md flex items-center gap-1 max-w-[150px]"
                      title={option.label || String(option.value)}
                    >
                      <span className="truncate">
                        {option.label || String(option.value)}
                      </span>
                      <span
                        className="material-icons !text-[14px] cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveOption(option);
                        }}
                      >
                        close
                      </span>
                    </span>
                  )}
                </div>
              ),
          )}
          {hiddenCount > 0 && (
            <span className="text-gray-500 whitespace-nowrap">
              +{hiddenCount} more
            </span>
          )}
        </div>
      );
    } else {
      // For single selection
      if (!selectedOptions) return placeholder;

      // If we have a label, use it, otherwise use the value
      const displayText =
        selectedOptions.label || String(selectedOptions.value);

      // Only return placeholder if we truly have nothing to display
      return displayText || placeholder;
    }
  };

  const handleClear = () => {
    const empty = multiple ? [] : "";
    setSelectedOptions(empty);
    if (onChange) {
      onChange(multiple ? [] : "");
    }
    setIsOpen(false);
  };

  // Check if an option is selected
  const isOptionSelected = (option) => {
    if (!option) return false;

    if (multiple) {
      return (
        Array.isArray(selectedOptions) &&
        selectedOptions.some(
          (item) => item && String(item.value) === String(option.value),
        )
      );
    } else {
      return (
        selectedOptions &&
        String(selectedOptions.value) === String(option.value)
      );
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        className={`w-full border flex justify-between items-center focus:outline-none ${
          sizeClasses[size]
        } ${fontSizeClasses[size]} ${rounded} 
         ${shadow ? "border-gray-50 inputShadow" : "border-gray-300"}
        ${
          disabled
            ? "border-neutrals-200 bg-neutrals-200 cursor-not-allowed text-black"
            : "bg-white hover:border-primary focus:border-primary"
        }
        ${noBorder ? "border-0" : ""}
        `}
        onClick={toggleDropdown}
        type="button"
        disabled={disabled}
        ref={buttonRef}
        {...rest}
      >
        <div
          className={`line-clamp-1 text-left flex-1 overflow-hidden ${
            (multiple && selectedOptions && selectedOptions.length > 0) ||
            (!multiple && selectedOptions?.value)
              ? "text-black"
              : "text-neutrals-500"
          }`}
        >
          {getDisplayValue()}
        </div>
        {showClearButton &&
          ((multiple && selectedOptions?.length > 0) ||
            (!multiple && selectedOptions)) &&
          !disabled && (
            <span
              className={`material-icons ${iconSizeClasses[size]} ml-1 flex-shrink-0 hover:text-red-500`}
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            >
              close
            </span>
          )}
        <span
          className={`material-icons transition-transform ${iconSizeClasses[size]} ml-1 flex-shrink-0`}
        >
          {isOpen ? "expand_less" : "expand_more"}
        </span>
      </button>
      {shouldPersist
        ? isOpen && (
            <div
              ref={dropdownContainerRef}
              style={{
                position: "fixed",
                width: buttonRef.current?.offsetWidth,
              }}
              className="z-[55] bg-white border border-gray-200 rounded-lg shadow-lg"
              {...rest}
            >
              {(showSearch || showButton) && (
                <div className="p-1 border-b border-gray-200">
                  {showSearch && (
                    <CustomSearch
                      placeholder="Search..."
                      value={searchValue !== "" ? searchValue : search}
                      onChange={
                        onSearch
                          ? (key, value) => onSearch(value)
                          : (key, value) => setSearch(value)
                      }
                      size={size}
                      rounded="rounded-full"
                      keyname="search"
                    />
                  )}
                  {showButton && (
                    <div className="flex justify-end mt-1">
                      <Button title={"Add New"} onClick={AddNew} icon={icon} />
                    </div>
                  )}
                </div>
              )}
              <div
                className="max-h-48 overflow-y-auto"
                onScroll={handleScroll}
                ref={refData}
              >
                {filteredOptions.length > 0 ? (
                  <ul>
                    {filteredOptions.map((option) => (
                      <li
                        key={option.value}
                        className={`p-2 flex justify-between items-center cursor-pointer ${
                          isOptionSelected(option)
                            ? "bg-primary text-white hover:bg-primary-700"
                            : "hover:bg-primary-50"
                        }`}
                        onClick={() => handleOptionSelect(option)}
                      >
                        <span className={`truncate`}>{option.label}</span>
                        {isOptionSelected(option) && (
                          <span className="material-icons !text-[16px]">
                            check
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="p-2 text-gray-500 text-center">
                    No options found
                  </div>
                )}
              </div>
            </div>
          )
        : isOpen &&
          createPortal(
            <div
              ref={dropdownContainerRef}
              style={{
                position: "fixed",
                width: buttonRef.current?.offsetWidth,
              }}
              className="z-[55] bg-white border border-gray-200 rounded-lg shadow-lg"
              {...rest}
            >
              {(showSearch || showButton) && (
                <div className="p-1 border-b border-gray-200">
                  {showSearch && (
                    <CustomSearch
                      placeholder="Search..."
                      value={searchValue !== "" ? searchValue : search}
                      onChange={
                        onSearch
                          ? (key, value) => onSearch(value)
                          : (key, value) => setSearch(value)
                      }
                      size={size}
                      rounded="rounded-full"
                      keyname="search"
                    />
                  )}
                  {showButton && (
                    <div className="flex justify-end mt-1">
                      <Button title={"Add New"} onClick={AddNew} icon={icon} />
                    </div>
                  )}
                </div>
              )}
              <div
                className="max-h-44 overflow-y-auto"
                onScroll={handleScroll}
                ref={refData}
              >
                {filteredOptions.length > 0 ? (
                  <ul>
                    {filteredOptions.map((option) => (
                      <li
                        key={option.value}
                        className={`p-2 text-sm! flex justify-between items-center cursor-pointer ${
                          isOptionSelected(option)
                            ? "bg-primary text-white hover:bg-primary-700"
                            : "hover:bg-primary-50"
                        }`}
                        onClick={() => handleOptionSelect(option)}
                      >
                        <span className={`truncate`}>{option.label}</span>
                        {isOptionSelected(option) && (
                          <span className="material-icons !text-[16px]">
                            check
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="p-2 text-gray-500 text-center">
                    No options found
                  </div>
                )}
              </div>
            </div>,
            document.querySelector(".select-root"),
          )}

      {/* Add Option Popup */}
      {showPopup &&
        createPortal(
          <AddOptionPopup
            onClose={() => setShowPopup(false)}
            onAdd={handleAddOption}
            existingOptions={customOptions}
            alertName={alertName}
          />,
          document.querySelector(".select-root"),
        )}
    </div>
  );
};

export default CustomSelect;
